import React, {
  useMemo,
} from 'react';
import uniq from 'lodash/uniq';
import compact from 'lodash/compact';
import {
  LeanClaimSummaryDTO,
} from 'dtos';
import Select from 'core/components/Select';
import {
  isValidTransition,
} from 'pages/Dashboard/pages/Billing/Charges/BulkClaim/utils';

type StatusOption = {
  status: string;
  eligibleCount: number;
};

function getStatusOptions(claims: LeanClaimSummaryDTO[]): StatusOption[] {
  const allStatuses = uniq(
    claims.flatMap((claim) => claim.validManualStatusTransitions ?? []),
  );
  return compact(allStatuses.map((status) => {
    const eligibleCount = claims.filter(
      (claim) => isValidTransition(claim, status),
    ).length;
    return eligibleCount > 0 ? { status, eligibleCount } : null;
  }));
}

type Props = {
  claimsList: LeanClaimSummaryDTO[];
  targetStatus: string | null;
  setTargetStatus: (status: string) => void;
};

export default function StatusSelect({
  claimsList,
  targetStatus,
  setTargetStatus,
}: Props) {
  const options = useMemo(() => getStatusOptions(claimsList), [claimsList]);
  const selectedOption = options.find((option) => option.status === targetStatus);

  return options.length > 0 ? (
    <Select<StatusOption>
      id="bulk-claim-target-status"
      label="Target status"
      required
      placeholder="Select"
      className="w-80 max-w-full"
      value={selectedOption}
      options={options}
      getValue={(option) => option.status}
      getLabel={(option) => (
        <div className="flex w-full items-center justify-between gap-4">
          <span>{option.status}</span>
          <span className="text-xs text-gray-500">{`${option.eligibleCount} eligible`}</span>
        </div>
      )}
      menuItemClassName="!text-sm"
      onChange={(option) => setTargetStatus((option as StatusOption).status)}
    />
  ) : (
    <div className="flex flex-col gap-1 items-center justify-center h-full mt-10">
      <p className="text-base text-gray-800 font-semibold">No claims to update.</p>
      <p className="text-sm text-gray-500 font-medium">Please go to claims page to select claims.</p>
    </div>
  );
}
